import React, { useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Image,
  Spinner,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import LoginModal from '../../common/loginModal/LoginModal';
import { API_BASE_URL } from '../../../config/serviceConfig';
import './Inregalo.css';
import AudioPlayerCard from '../../common/audioPlayerCard/AudioPlayerCard';
import heroImage from '../../../assets/images/in-regalo/in-regalo-profile.jpg';
import autoAnalisiImage from '../../../assets/images/in-regalo/auto-analisi.jpg';
import beSmartImage from '../../../assets/images/in-regalo/be-smart.jpg';
import energiaImage from '../../../assets/images/in-regalo/energia.jpg';

const GLOSSARY_ENDPOINT = `${API_BASE_URL}/glossario/download`;

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const InRegalo: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [glossaryLoading, setGlossaryLoading] = useState(false);
  const [glossaryError, setGlossaryError] = useState('');

  // Se loggato → vai diretto a Fit Anatomy dashboard
  // Se non loggato → apri LoginModal con tab accesso gratuito
  const handleDownloadRequest = () => {
    if (isAuthenticated) {
      navigate('/dashboard/fit-anatomy');
    } else {
      setShowModal(true);
    }
  };

  const handleGlossaryDownload = async () => {
    setGlossaryError('');
    setGlossaryLoading(true);
    try {
      const res = await fetch(GLOSSARY_ENDPOINT);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      downloadBlob(blob, 'glossario-benessere-posturale.pdf');
    } catch {
      setGlossaryError('Download non riuscito. Riprova più tardi.');
    } finally {
      setGlossaryLoading(false);
    }
  };

  return (
    <div className="in-regalo-container">
      {/* Hero Section */}
      <section className="in-regalo-hero">
        <Container>
          <Row className="align-items-center min-vh-60 py-5">
            <Col lg={6}>
              <div className="hero-content">
                <h1 className="hero-title display-3 fw-bold mb-4">
                  <span className="text-gradient-primary">In regalo</span>
                </h1>
                <p className="hero-description lead text-muted mb-4">
                  Le "istruzioni" che leggi, ascolti o guardi nella raccolta di
                  risorse in questa pagina sono un mio libero contributo al tuo
                  benessere e sono gratuite.
                  <br />
                  Sono indicazioni preziose che probabilmente nessuno ti ha
                  rivelato in questo modo, fino ad ora.
                  <br />
                  Te le rivelo io!
                  <br />
                  Il segreto per sentirti bene e più felice è a un click di
                  distanza. E' un tuo diritto sapere. E' un diritto di tutti!
                  <br />
                  Per questo sarò veramente felice quando vorrai condividerlo e
                  se vorrai scrivere un tuo commento o una recensione.
                  <br />
                  Condividi {'>'} Vivi meglio in un mondo più felice
                </p>
                <div className="hero-actions">
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={handleDownloadRequest}
                  >
                    Scarica tutte le risorse
                  </Button>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="hero-image position-relative">
                <div className="floating-element element-1 position-absolute"></div>
                <div className="floating-element element-2 position-absolute"></div>
                <div className="floating-element element-3 position-absolute"></div>
                <div className="main-image-placeholder text-center">
                  <img
                    src={heroImage}
                    alt="Annalisa - Formatrice di Benessere Posturale"
                    className="img-fluid rounded-circle shadow-lg"
                    style={{ maxWidth: '400px', border: '8px solid white' }}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Sezione Auto-analisi Posturale */}
      <section className="content-section py-5">
        <Container>
          {/* Box clickable */}
          <Row className="mb-5">
            <Col>
              <Card className="clickable-box border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <h3 className="fw-bold mb-0">
                    TI SPIEGO COME FARE L'AUTO-ANALISI POSTURALE A CASA!
                  </h3>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Sezione con immagine e testo */}
          <Row className="align-items-center mb-5">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h2 className="section-title fw-bold mb-4">
                Ci sono almeno 4 buoni motivi per imparare a fare l'auto-analisi
                posturale a casa:
              </h2>
              <div className="reasons-list mb-4">
                <div className="reason-item mb-3">
                  <strong>1- È estremamente ECONOMICA</strong>
                </div>
                <div className="reason-item mb-3">
                  <strong>
                    2- Tenere sotto controllo la tua postura ogni giorno ti
                    permette di PREVENIRE DOLORI
                  </strong>{' '}
                  muscolari e articolari derivanti da posture sbagliate e ti
                  accorgi subito se la tua postura si sta modificando
                </div>
                <div className="reason-item mb-3">
                  <strong>
                    3- Imparare ad osservare la tua immagine – senza giudizi –
                    aumenta la tua CONSAPEVOLEZZA
                  </strong>
                </div>
                <div className="reason-item mb-3">
                  <strong>
                    4- La consapevolezza della postura e del movimento aumenta
                    il BENESSERE A LIVELLO GLOBALE
                  </strong>
                </div>
              </div>
              <p className="mb-3">
                <strong>
                  Scarica subito il pdf che fa per te e inizia subito!
                </strong>
              </p>
              <p className="text-muted mb-3">
                Per cominciare ti serve solo uno specchio e una penna rossa per
                prendere nota sulla tua scheda.
              </p>
              <div className="d-flex flex-wrap gap-3 mb-3">
                <Button
                  variant="outline-secondary"
                  onClick={handleDownloadRequest}
                >
                  PER LEI
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={handleDownloadRequest}
                >
                  PER LUI
                </Button>
              </div>
              <Button variant="secondary" onClick={handleDownloadRequest}>
                VAI AL VIDEO
              </Button>
            </Col>
            {/* Prima immagine - Auto-analisi */}
            <Col lg={6}>
              <div className="content-image text-center">
                <Image
                  src={autoAnalisiImage}
                  alt="Auto-analisi posturale"
                  fluid
                  rounded
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Sezione Be Smart */}
      <section className="content-section py-5 bg-light">
        <Container>
          {/* Box clickable */}
          <Row className="mb-5">
            <Col>
              <Card
                className="clickable-box border-0 shadow-sm"
                onClick={handleDownloadRequest}
              >
                <Card.Body className="text-center p-4">
                  <h3 className="fw-bold mb-0">
                    SE STUDI O LAVORI IN SMART ... LEGGI SUBITO IL PRIMO
                    CAPITOLO DI "BE SMART"
                  </h3>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Descrizione Be Smart */}
          <Row className="mb-5">
            <Col lg={8} className="mx-auto">
              <p className="lead text-center mb-4">
                <strong>BE SMART</strong> è il manuale che ho preparato per chi
                studia o lavora "in smart" che salva la schiena, un sacco di
                soldi e anche il futuro!
              </p>
              <p className="mb-4">
                Perché, parliamoci chiaro: quando la schiena fa male non si
                riesce a fare più nulla in modo spensierato e con la stessa
                energia di prima, diventa tutto estremamente faticoso, doloroso
                e i nervi saltano subito.
              </p>
              <p className="mb-4">
                Di questi tempi anche i giovanissimi trascorrono già molte ore
                piegati sui dispositivi, anticipando di decenni dolori derivanti
                da posture distorte. Ecco perché questo manuale può essere
                l'assicurazione per il tuo futuro!
              </p>
              <p>
                Non è solo un manuale che puoi leggere in pdf,{' '}
                <strong>BE SMART</strong> è una collezione di oltre 60
                preziosissimi suggerimenti, di cui 27 esercizi pratici, spiegati
                con parole semplici attraverso 15 video e 3 tracce audio.
                Consigli preziosi da mettere in pratica PRIMA, per evitare di
                fare danni, ma anche suggerimenti e istruzioni per trovare
                sollievo POI in tanti modi diversi e non solo per la schiena, ma
                anche per il collo, per gli occhi, per le spalle, per le gambe,
                perfino per il pavimento pelvico!
              </p>
            </Col>
          </Row>

          {/* Sezione con immagine a sinistra */}
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0 order-lg-1">
              <div className="content-image text-center">
                <Image src={beSmartImage} alt="Be Smart" fluid rounded />
              </div>
            </Col>
            <Col lg={6} className="order-lg-2">
              <h3 className="fw-bold mb-4">
                Inizia subito a VIVERE MEGLIO: VIVI A TESTA ALTA
              </h3>
              <div className="download-options mb-4">
                <Button
                  variant="outline-secondary"
                  className="me-2 mb-2"
                  onClick={handleDownloadRequest}
                >
                  Scarica il pdf del Capitolo 1
                </Button>
                <Button
                  variant="outline-secondary"
                  className="me-2 mb-2"
                  onClick={handleDownloadRequest}
                >
                  Guarda subito il Video n. 1
                </Button>
                <Button
                  variant="outline-secondary"
                  className="me-2 mb-2"
                  onClick={handleDownloadRequest}
                >
                  Guarda subito il Video n. 2
                </Button>
                <Button
                  variant="outline-secondary"
                  className="me-2 mb-2"
                  onClick={handleDownloadRequest}
                >
                  Ascolta la traccia n. 1
                </Button>
              </div>
              <Button
                variant="secondary"
                onClick={() => navigate('/rimettiti-al-centro')}
              >
                SCOPRI GLI ALTRI VIDEOCORSI
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Sezione Energia */}
      <section className="content-section py-5 bg-dark text-white in-regalo-final-section">
        <Container>
          <Row className="mb-5">
            <Col>
              <Card className="clickable-box border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <h3 className="fw-bold mb-0">
                    RICARICATI DI ENERGIA IN SOLI 7 MINUTI – ASCOLTA LA MIA
                    TRACCIA AUDIO BONUS
                  </h3>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg={8} className="mx-auto">
              {/* Testo descrittivo sopra */}
              <div className="text-center mb-4">
                <p className="energy-text-white lead fw-400 mb-3">
                  Una visualizzazione guidata dalla mia voce, attraverso i
                  colori, in soli 7 minuti ti farà fare il pieno di energia per
                  affrontare la giornata, trovare sollievo o semplicemente fare
                  una breve pausa super rigenerante.
                </p>
                <p className="energy-text-white mb-4">
                  Non ci credi? Allora FAI SUBITO LA PROVA con il player qui
                  sotto!
                </p>
              </div>

              {/* Player Audio */}
              <div className="energy-player-vertical mb-4">
                <AudioPlayerCard />
              </div>

              {/* Immagine sotto il player */}
              <div className="text-center mt-4">
                <Image
                  src={energiaImage}
                  alt="Meditazione e ricarica energetica - visualizzazione guidata di 7 minuti"
                  fluid
                  rounded
                  className="energy-image-below"
                />
                <div className="image-caption mt-3">
                  <small className="energy-text-white">
                    Tecnica di visualizzazione guidata per ricaricare mente e
                    corpo in soli 7 minuti
                  </small>
                </div>
              </div>

              {/* Testo finale sotto l'immagine */}
              <div className="text-center mt-4">
                <p className="energy-text-white">
                  Scarica la traccia per ascoltarla quando vuoi, anche offline
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Sezione Glossario */}
      <section className="content-section py-5 bg-light">
        <Container>
          {/* Box clickable */}
          <Row className="mb-5">
            <Col>
              <Card className="clickable-box border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <h3 className="fw-bold mb-0">
                    CERCA NEL "GLOSSARIO" LE PAROLE DI CUI NON TI E' CHIARO IL
                    SIGNIFICATO
                  </h3>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg={8} className="mx-auto">
              <p className="text-center mb-4">
                Trova nel GLOSSARIO le parole tecniche di cui non ti è chiaro il
                significato e aumenta la tua CONSAPEVOLEZZA.
              </p>
              <p className="text-center mb-4">
                Ad esempio, scopri cos'è lo <strong>PSOAS</strong> e la sua
                fondamentale importanza
              </p>

              <div className="info-card p-4 rounded shadow-sm">
                <h5 className="fw-bold mb-3">
                  Perché è importante conoscere questi termini?
                </h5>
                <p className="mb-3">
                  Durante i corsi non c'è sufficiente tempo per spiegare nel
                  dettaglio l'impostazione del corpo prima del movimento, la
                  respirazione corretta da integrare, quello che bisognerebbe
                  sentire durante il movimento e, soprattutto, come respirare e
                  in che modo uscire da una posizione per evitare danni. Tutti
                  questi dettagli rallenterebbero troppo i ritmi di un
                  allenamento.
                </p>
                <p className="mb-3">
                  Ma senza tutti questi dettagli il rischio di muoversi male e
                  procurarsi dolori seri è molto alto.
                </p>
                <p className="mb-3">
                  Se poi la tua scelta è quella di allenarti a casa seguendo dei
                  video o un'app, il rischio di farti male è ancora più alto!
                </p>
                <p className="mb-4">
                  <strong>
                    PRIMA di allenarti da sola o di eseguire qualunque
                    esercizio, impara la corretta impostazione e respirazione.
                  </strong>
                </p>
                <div className="text-center">
                  <Button
                    variant="secondary"
                    onClick={handleGlossaryDownload}
                    disabled={glossaryLoading}
                  >
                    {glossaryLoading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Download in corso…
                      </>
                    ) : (
                      <>
                        <i className="bi bi-file-earmark-pdf me-2" />
                        Leggi il glossario
                      </>
                    )}
                  </Button>
                  {glossaryError && (
                    <p className="text-danger small mt-2 mb-0">
                      <i className="bi bi-exclamation-triangle-fill me-1" />
                      {glossaryError}
                    </p>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Sezione Contatti */}
      <section className="in-regalo-cta-section py-5 bg-dark text-white in-regalo-final-section">
        <div className="container">
          <div className="cta-content text-center py-4">
            <div className="d-flex flex-wrap justify-content-center gap-3">
              <Button
                variant="secondary"
                size="lg"
                className="cta-btn"
                as="a"
                href="/servizi"
              >
                SCOPRI GLI ALTRI MIEI SERVIZI
              </Button>

              <Button
                variant="outline-secondary"
                size="lg"
                className="cta-btn"
                as="a"
                href="/rimettiti-al-centro"
              >
                SCOPRI GLI ALTRI VIDEOCORSI
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* LoginModal con tab accesso gratuito */}
      <LoginModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onLoginSuccess={() => {
          setShowModal(false);
          navigate('/dashboard/fit-anatomy');
        }}
        showFreeAccessTab
        disableRedirect
      />
    </div>
  );
};

export default InRegalo;
